import React, { useState } from 'react';
import {
  BirdzDialog,
  BirdzModalTitle,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { IconButton, Typography } from '@mui/material';
import { AddCircle, Edit } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BTable from '../../../Tools/Table/BTable';
import ModalWithLoader, { RequestStatus } from '../../../Tools/ModalWithLoader/ModalWithLoader';
import useRefresh from '../../../../hooks/useRefresh';
import { capitalizeFirstLetter } from '../../../../utils';
import FabcompConfigurationForm, {
  FABCOMP_CONFIGURATIONS_ENDPOINT
} from './FabcompConfigurationForm';
import { FabcompConfig, RadioConfig } from './types';
import { formatFilterString } from './utils';

type FabcompConfigTableProps = {
  filterString: string;
  configurations: RadioConfig[];
};

export default function FabcompConfigTable({
  filterString,
  configurations
}: FabcompConfigTableProps) {
  const { t } = useTranslation();
  const { refresh, triggerRefresh } = useRefresh();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { notif, notifOptions } = useNotif();
  // null : modale fermée ; 'new' : ajout ; sinon la ligne en cours de modification
  const [selected, setSelected] = useState<FabcompConfig | 'new' | null>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const editedConfig = selected === 'new' ? null : selected;

  const deleteConfig = (config: FabcompConfig) =>
    confirmDialog({
      title: t('equipmentConfiguration.deleteFabcompConfiguration'),
      content: t('equipmentConfiguration.deleteFabcompConfigurationContent'),
      onValidate: () => {
        axios.delete(`${FABCOMP_CONFIGURATIONS_ENDPOINT}${config.id}/`).then(
          () => {
            triggerRefresh();
            notif({
              content: t('equipmentConfiguration.deleteFabcompConfigurationSuccess'),
              type: 'success'
            });
          },
          () => notif({ content: t('equipmentConfiguration.deleteError'), type: 'error' })
        );
        closeDialog();
      },
      onCancel: closeDialog
    });

  return (
    <>
      <Typography variant="overline" ml={4} mt={4}>
        {t('equipmentConfiguration.fabcompConfigurations')}
      </Typography>

      <BTable
        model="fabcomp-configurations"
        endpoint={FABCOMP_CONFIGURATIONS_ENDPOINT + formatFilterString(filterString, 'profile__')}
        refresh={refresh}
        fields={[
          { name: 'profile_dataset_code', label: t('equipmentConfiguration.datasetCodeLabel') },
          { name: 'profile_name', label: t('equipmentConfiguration.configuration') },
          { name: 'device_type', label: t('equipmentConfiguration.deviceType') },
          { name: 'fabcomp_label', label: t('equipmentConfiguration.fabcompLabel') }
        ]}
        actions={[
          {
            name: 'edit',
            label: t('equipmentConfiguration.editFabcompConfiguration'),
            render: (config: FabcompConfig) => (
              <IconButton onClick={() => setSelected(config)}>
                <Edit fontSize="small" color="action" />
              </IconButton>
            )
          },
          {
            name: 'delete',
            label: t('equipmentConfiguration.deleteFabcompConfiguration'),
            render: (config: FabcompConfig) => (
              <IconButton onClick={() => deleteConfig(config)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )
          }
        ]}
        headerActions={[
          {
            name: t('equipmentConfiguration.addFabcompConfiguration'),
            element: (
              <IconButton onClick={() => setSelected('new')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />

      <ModalWithLoader
        openModal={selected !== null}
        onClose={() => setSelected(null)}
        onSuccess={() => setSelected(null)}
        action={
          editedConfig
            ? t('equipmentConfiguration.editFabcompConfigurationAction')
            : t('equipmentConfiguration.createFabcompConfigurationAction')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        customErrorMessage={errorMessage}
        timeout={1500}
        header={
          <BirdzModalTitle>
            {editedConfig
              ? t('equipmentConfiguration.editFabcompConfiguration')
              : capitalizeFirstLetter(t('equipmentConfiguration.addFabcompConfiguration'))}
          </BirdzModalTitle>
        }
      >
        {selected !== null && (
          <FabcompConfigurationForm
            config={editedConfig}
            configurations={configurations}
            setRequestStatus={setRequestStatus}
            setErrorMessage={setErrorMessage}
            onSaved={triggerRefresh}
            onClose={() => setSelected(null)}
          />
        )}
      </ModalWithLoader>
      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />
    </>
  );
}
